import { useState } from "react";
import { SafePage } from "../components/SafePage";
import { AppButton } from "../components/AppButton";
import { openCrispChat } from "../shell/MonitoringBoot";

const SUPPORT_EMAIL = "support@getpickads.com";

type Faq = {
  q: string;
  a: string;
};

/** Order and copy from version9 Help.dc.html */
const FAQS: Faq[] = [
  {
    q: "I can’t see the popup",
    a: "You’re probably in the same country as your store. Use Preview as a visitor on Home to check any country.",
  },
  {
    q: "Prices didn’t change to the visitor’s currency",
    a: "Turn on local currencies for that market in Shopify.",
  },
  {
    q: "Visitors are moved twice, or never see the popup",
    a: "Shopify’s own automatic redirection may be on. Turn it off in Online Store, Preferences, then keep SafeGeo on Ask first.",
  },
  {
    q: "The popup disappeared after I changed theme",
    a: "Turn SafeGeo on again in the theme editor (App embeds) and Save. Check the status on Home.",
  },
  {
    q: "I used Shopify’s Geolocation app. What changes?",
    a: "SafeGeo recommends the right country and currency the same way, and asks first. Turn off other geo apps that redirect before SafeGeo runs.",
  },
  {
    q: "I got blocked from my own store",
    a: "Use the Staff access link in Settings, General. It skips country block and redirects for you and your team.",
  },
  {
    q: "A visitor sent me a reference code",
    a: "Block pages show a short code (like SG-7KQ2). Look it up in Block country or Settings, History and alerts to see the country and the rule.",
  },
  {
    q: "Testing with a VPN?",
    a: "SafeGeo remembers a visitor’s country for the browser session. Open a private window after changing VPN, or use Preview as a visitor.",
  },
  {
    q: "Why do redirected or blocked visitors still show in Shopify Analytics?",
    a: "Shopify counts a visit before any app runs. Use SafeGeo’s Activity and History to see what SafeGeo did.",
  },
  {
    q: "Does a visitor choosing a country count toward my limit?",
    a: "No. Only automatic redirects are counted. A visitor who picks a country and taps the button made their own choice.",
  },
  {
    q: "What happens when I reach my monthly limit?",
    a: "Rules that switch automatically fall back to Ask first. Nothing is blocked. The count resets on the 1st.",
  },
  {
    q: "I only have one market. What can I do?",
    a: "Market redirect needs at least two markets. You can still use Store redirect (Starter) to send visitors to another website.",
  },
];

export function HelpPage() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <SafePage
      screen="help"
      heading="Help"
      primaryAction={
        <AppButton slot="primary-action" onClick={() => openCrispChat()}>
          Chat with us
        </AppButton>
      }
    >
      <s-section heading="Questions? A real person reads every message.">
        <s-stack gap="base">
          <s-paragraph color="subdued">
            Chat with us is the fastest way. If we are away, we reply by email
            within 24 hours. We can already see your store, your plan and the
            page you were on, so you don’t have to repeat them.
          </s-paragraph>
          <s-paragraph color="subdued">
            Use Chat with us at the top, or email{" "}
            <s-link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</s-link>
          </s-paragraph>
          <s-paragraph color="subdued">
            Helpful to include: the visitor’s country and the reference code
            (SG-XXXX) if they have one.
          </s-paragraph>
        </s-stack>
      </s-section>

      <s-section heading="Common questions">
        <s-box borderWidth="base" borderRadius="base" padding="none">
          <s-stack gap="none">
            {FAQS.map((faq, i) => {
              const open = openIndex === i;
              return (
                <s-box
                  key={faq.q}
                  padding="base"
                  borderBlockStartWidth={i === 0 ? undefined : "base"}
                >
                  <s-stack gap="small">
                    <s-clickable
                      onClick={() => setOpenIndex(open ? -1 : i)}
                    >
                      <s-stack
                        direction="inline"
                        gap="base"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <s-text type="strong">{faq.q}</s-text>
                        <s-text color="subdued">{open ? "−" : "+"}</s-text>
                      </s-stack>
                    </s-clickable>
                    {open ? (
                      <s-paragraph color="subdued">{faq.a}</s-paragraph>
                    ) : null}
                  </s-stack>
                </s-box>
              );
            })}
          </s-stack>
        </s-box>
      </s-section>

      <s-paragraph color="subdued">
        SafeGeo works with Online Store themes. Headless storefronts aren’t
        supported.
      </s-paragraph>
    </SafePage>
  );
}
